import React, { useState, useEffect } from "react";
import secureLocalStorage from "react-secure-storage";
import { useDarkMode } from "../context/darkModeContext";

import PasswordChangeForm from "../components/settings/PasswordChangeForm";
import TeamManagement from "../components/settings/TeamManagement";
import DeviceInfo from "../components/settings/DeviceInfo";
import Lobby from "../components/settings/Lobby";
import Departments from "../components/settings/Departments";
import Subscription from "../components/settings/Subscription";

import {
  User,
  Monitor,
  Users,
  Building2,
  Home,
  CreditCard,
  ChevronUp,
  ChevronDown,
  X,
} from "lucide-react";

const navigationItems = [
  {
    key: "PasswordChangeForm",
    label: "Account Settings",
    description: "Email, password & profile",
    icon: User,
    color: "indigo",
    border: "border-indigo-500",
    bg: "from-indigo-50 to-violet-50",
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600",
  },

  {
    key: "DeviceInfo",
    label: "Device Info",
    description: "Connected devices & sessions",
    icon: Monitor,
    color: "cyan",
    border: "border-cyan-500",
    bg: "from-cyan-50 to-sky-50",
    iconBg: "bg-cyan-100",
    iconColor: "text-cyan-600",
  },

  {
    key: "TeamManagement",
    label: "Team Management",
    description: "Members, roles & permissions",
    icon: Users,
    color: "orange",
    border: "border-orange-500",
    bg: "from-orange-50 to-amber-50",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
  },

  {
    key: "Department",
    label: "Departments",
    description: "Structure & access groups",
    icon: Building2,
    color: "purple",
    border: "border-purple-500",
    bg: "from-purple-50 to-fuchsia-50",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
  },

  {
    key: "Lobby",
    label: "Lobby",
    description: "Guest access & waiting room",
    icon: Home,
    color: "green",
    border: "border-green-500",
    bg: "from-green-50 to-emerald-50",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
  },

  {
    key: "Subscription",
    label: "Subscription",
    description: "Plan, billing & usage",
    icon: CreditCard,
    color: "amber",
    border: "border-amber-500",
    bg: "from-amber-50 to-yellow-50",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
  },
];

const ROLE_ACCESS = {
  employee: ["PasswordChangeForm"],

  super_admin: ["PasswordChangeForm", "Lobby"],

  org_admin: [
    "PasswordChangeForm",
    "DeviceInfo",
    "TeamManagement",
    "Department",
    "Lobby",
    "Subscription",
  ],
};

const SettingsPage = () => {
  const [selectedNavItem, setSelectedNavItem] = useState(null);
  const [role, setRole] = useState("");
  const { darkMode } = useDarkMode();

  useEffect(() => {
    let data = JSON.parse(secureLocalStorage.getItem("profileData"));
    setRole(data?.role_priv);

    // Default open
    if (data?.role_priv === "employee") {
      setSelectedNavItem("PasswordChangeForm");
    }

    if (data?.role_priv === "super_admin") {
      setSelectedNavItem("PasswordChangeForm");
    }

    if (data?.role_priv === "org_admin") {
      setSelectedNavItem("PasswordChangeForm");
    }
  }, []);

  const handleNavItemClick = (navItem) => {
    setSelectedNavItem(navItem);
  };

  const renderSelectedComponent = () => {
    switch (selectedNavItem) {
      case "PasswordChangeForm":
        return <PasswordChangeForm />;

      case "DeviceInfo":
        return <DeviceInfo />;

      case "TeamManagement":
        return <TeamManagement />;

      case "Department":
        return <Departments />;

      case "Lobby":
        return <Lobby />;

      case "Subscription":
        return <Subscription />;

      default:
        return null;
    }
  };

  const filteredNavigationItems = navigationItems.filter((item) =>
    ROLE_ACCESS[role]?.includes(item.key)
  );

  const activeItem = navigationItems.find(
    (item) => item.key === selectedNavItem
  );

  return (
    <div
      className={`w-full min-h-screen px-10 py-6 transition-all duration-300 ${
        darkMode ? "bg-[#0F172A] text-white" : "bg-[#F5F7FB] text-[#111827]"
      }`}
    >
      {/* HEADER */}
      <div className="mb-6">
        <h1
          className={`text-[42px] leading-[60px] font-bold ${
            darkMode ? "text-white" : "text-[#0f172a]"
          }`}
        >
          Settings
        </h1>

        <p
          className={`mt-1 text-[17px] font-medium ${
            darkMode ? "text-slate-400" : "text-[#667085]"
          }`}
        >
          Manage your workspace, account, and preferences
        </p>
      </div>

      {/* SETTINGS CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredNavigationItems.map((item) => {
          const Icon = item.icon;

          const isActive = selectedNavItem === item.key;

          return (
            <div
              key={item.key}
              onClick={() => setSelectedNavItem(isActive ? null : item.key)}
              className={`
                relative overflow-hidden rounded-[24px] border ${darkMode ? "bg-[#1E293B]" : "bg-white"}
                transition-all duration-300 cursor-pointer
                hover:shadow-lg hover:-translate-y-1
                ${
                  isActive
                    ? `${item.border} shadow-md ring-4 ring-white`
                    : `${darkMode ? "border-slate-700" : "border-gray-200"}`
                }
              `}
            >
              {/* TOP BORDER */}
              {/* <div
                className={`h-1.5 w-full ${
                  isActive ? item.border.replace("border", "bg") : "bg-gray-100"
                }`}
              /> */}

              <div className="px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className={`
                      w-12 h-12 rounded-2xl flex items-center justify-center
                      ${item.iconBg}
                    `}
                  >
                    <Icon className={`w-6 h-6 ${item.iconColor}`} />
                  </div>

                  <div>
                    <h3
                      className={`text-[17px] font-semibold ${darkMode ? "text-white" : "text-[#111827]"}`}
                    >
                      {item.label}
                    </h3>

                    <p className="text-[14px] text-gray-500 mt-1">
                      {item.description}
                    </p>
                  </div>
                </div>

                <div>
                  {isActive ? (
                    <ChevronUp className="text-gray-500" />
                  ) : (
                    <ChevronDown className="text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ACTIVE PANEL */}
      {selectedNavItem && activeItem && (
        <div
          className={`
            mt-5 rounded-[28px] overflow-hidden border
            ${darkMode ? "bg-[#0B1730] border-slate-700" : "bg-white shadow-sm"}
            ${activeItem.border}
          `}
        >
          {/* PANEL HEADER */}
          <div
            className={`
              px-5 py-4 border-b flex items-center justify-between
           ${darkMode ? "bg-[#16213E]" : `bg-gradient-to-r ${activeItem.bg}`}}
            `}
          >
            <div className="flex items-center gap-4">
              <div
                className={`
                  w-12 h-12 rounded-2xl flex items-center justify-center
                  ${activeItem.iconBg}
                `}
              >
                <activeItem.icon
                  className={`w-6 h-6 ${activeItem.iconColor}`}
                />
              </div>

              <div>
                <h2
                  className={`text-[24px] font-bold ${darkMode ? "text-white" : "text-[#111827]"}`}
                >
                  {activeItem.label}
                </h2>

                <p className="text-gray-500 text-[14px] mt-1">
                  {activeItem.description}
                </p>
              </div>
            </div>

            <button
              onClick={() => setSelectedNavItem(null)}
              className={`w-10 h-10 rounded-full ${
                darkMode
                  ? "bg-slate-700 hover:bg-slate-600"
                  : "bg-black/5 hover:bg-black/10"
              } flex items-center justify-center transition`}
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* PANEL BODY */}
          <div
            className={` ${darkMode ? "bg-[#0F172A] text-white" : "bg-white text-[#111827]"} `}
          >
            {renderSelectedComponent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
